import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CompanyResponse, FeedPostResponse } from '../../../../@shared/models/response.module';
import { FeedService } from '../../../../@shared/services/feed.service';
import { ToastService } from '../../../../@core/services/toast.service';

@Component({
  selector: 'app-company-profile-feed',
  standalone: false,
  templateUrl: './company-profile-feed.html',
  styleUrl: './company-profile-feed.css'
})
export class CompanyProfileFeed implements OnInit {
  @Input() company!: CompanyResponse;
  @Output() dataChanged = new EventEmitter<void>();

  feedPosts: FeedPostResponse[] = [];
  loading: boolean = false;

  // Create/Edit modal
  showModal: boolean = false;
  editingPost: FeedPostResponse | null = null;
  postTitle: string = '';
  postDescription: string = '';
  saving: boolean = false;

  // Image upload
  selectedImages: File[] = [];
  selectedImagePreviews: string[] = [];
  uploadingImages: boolean = false;

  // Lightbox
  showLightbox: boolean = false;
  lightboxImages: string[] = [];
  lightboxIndex: number = 0;

  constructor(
    private feedService: FeedService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadFeedPosts();
  }

  loadFeedPosts() {
    this.loading = true;
    this.feedService.getFeedPosts().subscribe({
      next: (response) => {
        this.feedPosts = response.data || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  openCreateModal() {
    this.editingPost = null;
    this.postTitle = '';
    this.postDescription = '';
    this.selectedImages = [];
    this.showModal = true;
  }

  openEditModal(post: FeedPostResponse) {
    this.editingPost = post;
    this.postTitle = post.title;
    this.postDescription = post.description || '';
    this.selectedImages = [];
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.editingPost = null;
    this.postTitle = '';
    this.postDescription = '';
    // Revoke object URLs to free memory
    this.selectedImagePreviews.forEach(url => URL.revokeObjectURL(url));
    this.selectedImages = [];
    this.selectedImagePreviews = [];
  }

  onImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files);
      // Add to existing selection
      this.selectedImages = [...this.selectedImages, ...newFiles];
      // Generate preview URLs for new files
      const newPreviews = newFiles.map(file => URL.createObjectURL(file));
      this.selectedImagePreviews = [...this.selectedImagePreviews, ...newPreviews];
    }
    // Reset input so the same file can be selected again
    input.value = '';
  }

  removeSelectedImage(index: number) {
    // Revoke the object URL before removing
    URL.revokeObjectURL(this.selectedImagePreviews[index]);
    this.selectedImages.splice(index, 1);
    this.selectedImagePreviews.splice(index, 1);
  }

  async savePost() {
    if (!this.postTitle.trim()) {
      this.toastService.error(this.translateService.instant('companyProfile.feed.validationError'));
      return;
    }

    this.saving = true;

    try {
      let post: FeedPostResponse;

      if (this.editingPost) {
        // Update existing post
        post = await this.feedService.updateFeedPost({
          feed_post_id: this.editingPost.feed_post_id,
          title: this.postTitle,
          description: this.postDescription
        }).toPromise() as FeedPostResponse;
      } else {
        // Create new post
        post = await this.feedService.createFeedPost({
          title: this.postTitle,
          description: this.postDescription
        }).toPromise() as FeedPostResponse;
      }

      // Upload images if any
      if (this.selectedImages.length > 0) {
        for (const image of this.selectedImages) {
          await this.feedService.addImage(post.feed_post_id, image).toPromise();
        }
      }

      this.toastService.success(this.translateService.instant(
        this.editingPost ? 'companyProfile.feed.updateSuccess' : 'companyProfile.feed.createSuccess'
      ));
      this.closeModal();
      this.loadFeedPosts();
      this.dataChanged.emit();
    } catch (error) {
      console.error('Error saving post:', error);
      this.toastService.error(this.translateService.instant('companyProfile.feed.saveError'));
    } finally {
      this.saving = false;
    }
  }

  async deletePost(post: FeedPostResponse) {
    if (!confirm(this.translateService.instant('companyProfile.feed.deleteConfirm'))) return;

    try {
      await this.feedService.deleteFeedPost(post.feed_post_id).toPromise();
      this.toastService.success(this.translateService.instant('companyProfile.feed.deleteSuccess'));
      this.loadFeedPosts();
      this.dataChanged.emit();
    } catch (error) {
      console.error('Error deleting post:', error);
      this.toastService.error(this.translateService.instant('companyProfile.feed.deleteError'));
    }
  }

  async deleteImage(post: FeedPostResponse, imageId: number) {
    if (!confirm(this.translateService.instant('companyProfile.feed.deleteImageConfirm'))) return;

    try {
      await this.feedService.deleteImage(imageId).toPromise();
      // Update the editingPost images array immediately for instant UI feedback
      if (this.editingPost && this.editingPost.feed_post_id === post.feed_post_id) {
        this.editingPost.images = this.editingPost.images.filter(img => img.image_id !== imageId);
      }
      // Also update the feedPosts array
      const postIndex = this.feedPosts.findIndex(p => p.feed_post_id === post.feed_post_id);
      if (postIndex !== -1) {
        this.feedPosts[postIndex].images = this.feedPosts[postIndex].images.filter(img => img.image_id !== imageId);
      }
      this.toastService.success(this.translateService.instant('companyProfile.feed.imageDeleted'));
    } catch (error) {
      console.error('Error deleting image:', error);
      this.toastService.error(this.translateService.instant('companyProfile.feed.deleteImageError'));
    }
  }

  openLightbox(post: FeedPostResponse, index: number) {
    this.lightboxImages = post.images.map(img => img.image_url);
    this.lightboxIndex = index;
    this.showLightbox = true;
  }

  closeLightbox() {
    this.showLightbox = false;
    this.lightboxImages = [];
    this.lightboxIndex = 0;
  }

  previousImage() {
    if (this.lightboxIndex > 0) {
      this.lightboxIndex--;
    } else {
      this.lightboxIndex = this.lightboxImages.length - 1;
    }
  }

  nextImage() {
    if (this.lightboxIndex < this.lightboxImages.length - 1) {
      this.lightboxIndex++;
    } else {
      this.lightboxIndex = 0;
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
